import {
  GENERATION_PRICING_UNITS,
  type GenerationConnectorAccounting,
  type GenerationPricing,
  type GenerationPricingUnit,
} from '@mpr/shared';

/**
 * The connector form's price fields, as text the person types, and back.
 * Kept apart from the component so the conversion, which decides what is
 * saved, can be tested on its own.
 */

/** The resolutions a video price can vary by (the quality tiers' own names). */
export const PRICING_RESOLUTIONS = ['480P', '720P', '1080P'] as const;

export const PRICING_UNITS: readonly GenerationPricingUnit[] = GENERATION_PRICING_UNITS;

export interface PricingForm {
  /** '' is "no price". */
  unit: GenerationPricingUnit | '';
  price: string;
  inputPrice: string;
  outputPrice: string;
  soundPrice: string;
  byResolution: Record<(typeof PRICING_RESOLUTIONS)[number], string>;
  usageInputTokensPath: string;
  usageOutputTokensPath: string;
  usageSecondsPath: string;
}

export const blankPricingForm: PricingForm = {
  unit: '',
  price: '',
  inputPrice: '',
  outputPrice: '',
  soundPrice: '',
  byResolution: { '480P': '', '720P': '', '1080P': '' },
  usageInputTokensPath: '',
  usageOutputTokensPath: '',
  usageSecondsPath: '',
};

const text = (value: number | undefined): string => (value === undefined ? '' : String(value));

/** From a connector row or a preset (which may carry none of it). */
export function pricingFormFrom(source: Partial<GenerationConnectorAccounting> | null | undefined): PricingForm {
  const pricing = source?.pricing ?? null;
  return {
    unit: pricing?.unit ?? '',
    price: text(pricing?.price),
    inputPrice: text(pricing?.inputPrice),
    outputPrice: text(pricing?.outputPrice),
    soundPrice: text(pricing?.soundPrice),
    byResolution: {
      '480P': text(pricing?.byResolution?.['480P']),
      '720P': text(pricing?.byResolution?.['720P']),
      '1080P': text(pricing?.byResolution?.['1080P']),
    },
    usageInputTokensPath: source?.usageInputTokensPath ?? '',
    usageOutputTokensPath: source?.usageOutputTokensPath ?? '',
    usageSecondsPath: source?.usageSecondsPath ?? '',
  };
}

/** A price typed as text: a non-negative number, or null when blank, or undefined when it is not a number. */
function parsePrice(value: string): number | null | undefined {
  const trimmedValue = value.trim().replace(',', '.');
  if (trimmedValue === '') return null;
  const number = Number(trimmedValue);
  return Number.isFinite(number) && number >= 0 ? number : undefined;
}

export interface PricingBody {
  pricing: GenerationPricing | null;
  usageInputTokensPath: string | null;
  usageOutputTokensPath: string | null;
  usageSecondsPath: string | null;
}

/**
 * The fields to save. `invalid` is true when a price is typed but is not a
 * non-negative number; a unit with no price at all saves as "no price" rather
 * than a pricing that prices nothing.
 */
export function pricingFromForm(form: PricingForm): { body: PricingBody; invalid: boolean } {
  const path = (value: string): string | null => value.trim() || null;
  const paths = {
    usageInputTokensPath: path(form.usageInputTokensPath),
    usageOutputTokensPath: path(form.usageOutputTokensPath),
    usageSecondsPath: path(form.usageSecondsPath),
  };
  if (form.unit === '') return { body: { pricing: null, ...paths }, invalid: false };

  let invalid = false;
  const read = (value: string): number | undefined => {
    const parsed = parsePrice(value);
    if (parsed === undefined) invalid = true;
    return parsed ?? undefined;
  };

  const pricing: GenerationPricing = { unit: form.unit, currency: 'USD' };
  if (form.unit === 'TOKENS_1M') {
    const inputPrice = read(form.inputPrice);
    const outputPrice = read(form.outputPrice);
    if (inputPrice !== undefined) pricing.inputPrice = inputPrice;
    if (outputPrice !== undefined) pricing.outputPrice = outputPrice;
  } else {
    const price = read(form.price);
    if (price !== undefined) pricing.price = price;
    if (form.unit === 'SECOND') {
      const soundPrice = read(form.soundPrice);
      if (soundPrice !== undefined) pricing.soundPrice = soundPrice;
      const byResolution: Record<string, number> = {};
      for (const resolution of PRICING_RESOLUTIONS) {
        const value = read(form.byResolution[resolution]);
        if (value !== undefined) byResolution[resolution] = value;
      }
      if (Object.keys(byResolution).length > 0) pricing.byResolution = byResolution;
    }
  }

  const hasNumber =
    pricing.price !== undefined ||
    pricing.inputPrice !== undefined ||
    pricing.outputPrice !== undefined ||
    pricing.byResolution !== undefined;
  return { body: { pricing: hasNumber ? pricing : null, ...paths }, invalid };
}

/** The suffixes a price tag ends with, per unit, in the reader's language. */
export type PriceSuffixes = Record<GenerationPricingUnit, string>;

const short = (value: number): string => String(Number(value.toFixed(4)));

/**
 * A short tag for the connectors table: "$0.05-0.20/s", "$0.03/img",
 * "$2 / $6 per 1M". Null when the connector has no price.
 */
export function pricingTag(pricing: GenerationPricing | null | undefined, suffixes: PriceSuffixes): string | null {
  if (!pricing) return null;
  const suffix = suffixes[pricing.unit];
  if (pricing.unit === 'TOKENS_1M') {
    if (pricing.inputPrice === undefined && pricing.outputPrice === undefined) return null;
    return `$${short(pricing.inputPrice ?? 0)} / $${short(pricing.outputPrice ?? 0)}${suffix}`;
  }
  const values = [
    ...(pricing.price !== undefined ? [pricing.price] : []),
    ...Object.values(pricing.byResolution ?? {}),
  ];
  if (values.length === 0) return null;
  const low = Math.min(...values);
  const high = Math.max(...values);
  return low === high ? `$${short(low)}${suffix}` : `$${short(low)}-${short(high)}${suffix}`;
}
